import { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { ColumnDef } from "@tanstack/react-table";

import { DataTable } from "components/DataTable/DataTable";
import { useNavigate } from "hooks/useNavigate";
import { ISubject } from "interfaces/subject.interface";
import { IZone } from "interfaces/zone.interface";

import { DeviceStatus, Sex } from "../../components/TableRowComponents";

type SubjectTableRow = {
  id: string;
  subjectId: string | null;
  sex: string | null;
  birthYear: string | null;
  deviceName: string | null;
  zoneName: string | null;
  deviceSerialNumber: string | null;
  deviceStatus: boolean | null;
};

type IProps = {
  subjects: ISubject[];
  zoneEntity: Record<string, IZone>;
};

export function SubjectList({ subjects, zoneEntity }: IProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const columns = useMemo<ColumnDef<SubjectTableRow, any>[]>(
    () => [
      {
        accessorKey: "subjectId",
        header: t("Subject Id"),
      },
      {
        accessorKey: "zoneName",
        header: t("Zone"),
      },
      {
        accessorKey: "sex",
        header: t("Sex"),
        cell: Sex,
      },
      {
        accessorKey: "birthYear",
        header: t("Birth Year"),
      },
      {
        accessorKey: "deviceName",
        header: t("Device Name / Location"),
      },
      {
        accessorKey: "deviceSerialNumber",
        header: t("Serial Number"),
      },
      {
        accessorKey: "deviceStatus",
        header: t("Device Status"),
        cell: DeviceStatus,
        sortingFn: (rowA, rowB) => {
          const rank = [true, false, null];

          return (
            rank.indexOf(rowA.original.deviceStatus) -
            rank.indexOf(rowB.original.deviceStatus)
          );
        },
      },
    ],
    [t],
  );

  const data = useMemo(
    (): SubjectTableRow[] =>
      subjects.map((s) => {
        const device = s.devices?.data?.[0];

        return {
          id: s.id,
          subjectId: s.identifier ?? null,
          birthYear: s.birth_year?.toString() ?? null,
          sex: s.sex ?? null,
          deviceName: device?.name ?? null,
          deviceSerialNumber: device?.serial_number ?? null,
          deviceStatus: device?.online ?? null,
          zoneName: s.zone_id ? (zoneEntity[s.zone_id]?.name ?? null) : null,
        };
      }),
    [subjects, zoneEntity],
  );

  return (
    <DataTable
      tableId={"subject-list"}
      columns={columns}
      data={data}
      getRowId={(row) => row.id}
      onRowClick={(row) => navigate(row.id)}
    />
  );
}
